import { useEffect, useRef } from 'react';
import { useFormContext } from '../context/FormContext';
import { useAuth } from '../context/AuthContext';
import { saveAssessmentData } from '../firebase';

const useAutoSave = (assessmentId) => {
  const { formData, hasUnsavedChanges, setHasUnsavedChanges, setLastSaved } = useFormContext();
  const { currentUser } = useAuth();
  const saveTimeoutRef = useRef(null);
  const lastSavedDataRef = useRef(JSON.stringify(formData));

  // Detect changes in form data for auto-save
  useEffect(() => {
    // Skip if no user or assessment ID
    if (!currentUser || !assessmentId) return;

    // Compare stringified data to check for actual changes
    const currentData = JSON.stringify(formData);
    if (currentData !== lastSavedDataRef.current) {
      setHasUnsavedChanges(true);
      
      // Clear any existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      // Set a new timeout for auto-saving (2 seconds after typing stops)
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          await saveAssessmentData(assessmentId, currentUser.uid, formData);
          lastSavedDataRef.current = currentData;
          setHasUnsavedChanges(false);
          setLastSaved(new Date());
        } catch (error) {
          console.error('Auto-save failed:', error);
        }
      }, 2000);
    }
    
    // Cleanup on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [formData, currentUser, assessmentId, setHasUnsavedChanges, setLastSaved]);

  // Save before user leaves the page
  useEffect(() => {
    const handleBeforeUnload = async (e) => {
      if (hasUnsavedChanges && currentUser && assessmentId) {
        // Standard browser behavior to prompt user
        e.preventDefault();
        e.returnValue = '';
        
        // Attempt to save
        try {
          await saveAssessmentData(assessmentId, currentUser.uid, formData);
        } catch (error) {
          console.error('Final save before unload failed:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, formData, currentUser, assessmentId]);

  // Manual save function for save button
  const manualSave = async () => {
    if (currentUser && assessmentId) {
      try {
        await saveAssessmentData(assessmentId, currentUser.uid, formData);
        lastSavedDataRef.current = JSON.stringify(formData);
        setHasUnsavedChanges(false);
        setLastSaved(new Date());
        return true;
      } catch (error) {
        console.error('Manual save failed:', error);
        return false;
      }
    }
    return false;
  };

  return { manualSave, hasUnsavedChanges };
};

export default useAutoSave;